import { useState } from 'react';
import { Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomCellRendererProps } from 'ag-grid-react';
import { CellEditRequestEvent, ColDef, ColumnResizedEvent, ValueFormatterParams } from 'ag-grid-community';
import { InfniteScrollingGrid } from '../../AgGrid/InfiniteScrollingGrid';
import {
  getAlignmentSetting,
  searchAlignmentSettings,
  searchPropertyMappingsForAlignments,
} from '../../../api/arcgis-civil3d-alignment-setting.api';
import { useSearchAlignmentStyles } from '../../../hooks/api/use-alignment-styles.hook';
import {
  useUpdateAlignmentSetting,
  useDeleteAlignmentSettings,
  useDuplicateAlignmentSettings,
  useCreatePropertyMappingsForAlignments,
} from '../../../hooks/api/use-arcgis-civil3d-alignment-settings.hook';
import { civil3dFeatureTypesByCategory, Civil3dFeatureType } from '../../../../../types/constants/civil3d-feature-type';
import { ArrayFilter } from '../../AgGrid/ArrayFilter';
import {
  ArcgisCivil3dAlignmentSettingApiModel,
  ArcgisCivil3dAlignmentSettingRelationship,
  WithAlignmentStyle,
} from '../../../../../types/models/arcgis-civil3d-alignment-setting.model';
import { ArcgisCivil3dConfigurationGrid } from '../ArcgisCivil3dConfigurationGrid';
import {
  WithArcgisFeatureClass,
  WithAutodeskLayer,
  WithConfiguration,
} from '../../../../../types/models/arcgis-civil3d-setting.model';

export const AlignmentConfigurationGrid = () => {
  const navigate = useNavigate();
  const { configurationSettingId } = useParams<{ configurationSettingId: string }>();
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  const [updateAlignmentSetting, { error: updateError }] = useUpdateAlignmentSetting();
  const [deleteAlignmentSettings, { error: deleteError }] = useDeleteAlignmentSettings();
  const [duplicateAlignmentSettings, { error: duplicateError }] = useDuplicateAlignmentSettings();
  const [createPropertyMappings, { error: createPropertyMappingsError }] = useCreatePropertyMappingsForAlignments();

  const { loading: loadingAlignmentStyles, response: alignmentStylesResponse } = useSearchAlignmentStyles({
    limit: 999999,
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });

  const alignmentStyleIdNameMap = alignmentStylesResponse?.pages
    .flatMap(({ result }) => result)
    .reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {} as Record<string, string>);

  const columns: ColDef<
    ArcgisCivil3dAlignmentSettingApiModel &
      WithConfiguration &
      WithArcgisFeatureClass &
      WithAutodeskLayer &
      WithAlignmentStyle
  >[] = [
    {
      headerName: 'Style',
      field: 'alignmentStyle',
      colId: 'alignmentStyle.name',
      valueGetter: ({ data }) => data?.alignmentStyle?.id,
      editable: true,
      sortable: true,
      flex: 0,
      width: columnWidths['alignmentStyle.name'] ? Number(columnWidths['alignmentStyle.name']) : 200,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: alignmentStylesResponse?.pages.flatMap(({ result }) => result).map(({ id }) => id) ?? [],
        formatValue: (value: number) => alignmentStyleIdNameMap?.[value] ?? '',
      },
      valueFormatter: (params: ValueFormatterParams<ArcgisCivil3dAlignmentSettingApiModel>) =>
        alignmentStyleIdNameMap?.[params.value] ?? '',
    },
    {
      headerName: 'Feature Type',
      field: 'civil3dFeatureType',
      colId: 'civil3dFeatureType.name',
      width: columnWidths['civil3dFeatureType.name'] ? Number(columnWidths['civil3dFeatureType.name']) : 200,
      flex: 0,
      valueGetter: ({ data }: { data?: ArcgisCivil3dAlignmentSettingApiModel }) => data?.civil3dFeatureType,
      filter: ArrayFilter,
      editable: true,
      sortable: true,
      filterParams: { values: civil3dFeatureTypesByCategory[Civil3dFeatureType.FEATURE_LINE] },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: civil3dFeatureTypesByCategory[Civil3dFeatureType.FEATURE_LINE],
      },
    },
    {
      headerName: 'Property Mappings',
      width: columnWidths.propertyMappings ? Number(columnWidths.propertyMappings) : 250,
      flex: 0,
      cellRenderer: ({ node }: CustomCellRendererProps<ArcgisCivil3dAlignmentSettingApiModel>) => (
        <Button variant="text" onClick={() => navigate(`${node.id}/property-mappings`)} sx={{ textTransform: 'none' }}>
          View property mapping
        </Button>
      ),
    },
  ];

  const onColumnResized = (params: ColumnResizedEvent) => {
    setColumnWidths((prevColumnWidths) =>
      params.column
        ? {
            ...(prevColumnWidths ?? {}),
            [params.column.getColId()]: params.column.getActualWidth(),
          }
        : prevColumnWidths,
    );
  };

  const getAlignmentSettings: Parameters<
    typeof InfniteScrollingGrid<
      ArcgisCivil3dAlignmentSettingApiModel &
        WithConfiguration &
        WithArcgisFeatureClass &
        WithAutodeskLayer &
        WithAlignmentStyle
    >
  >[0]['onGetRows'] = async ({ cursor, limit, filterQuery, sortQuery }) => {
    const { data } = await searchAlignmentSettings<
      ArcgisCivil3dAlignmentSettingApiModel &
        WithConfiguration &
        WithArcgisFeatureClass &
        WithAutodeskLayer &
        WithAlignmentStyle
    >({
      ...filterQuery,
      sort: sortQuery,
      cursor,
      limit,
      include: [
        ArcgisCivil3dAlignmentSettingRelationship.AUTODESK_LAYER,
        ArcgisCivil3dAlignmentSettingRelationship.ARCGIS_FEATURE_CLASS,
        ArcgisCivil3dAlignmentSettingRelationship.CONFIGURATION,
        ArcgisCivil3dAlignmentSettingRelationship.ALIGNMENT_STYLE,
      ],
      configurationId: Number(configurationSettingId),
    });

    return data;
  };

  const updateSetting = async ({ colDef, data, newValue }: CellEditRequestEvent) => {
    if (!colDef?.field || !data?.id || colDef.field === 'query') return;

    const relationFieldMap = {
      autodeskLayer: 'autodeskLayerId',
      arcgisFeatureClass: 'arcgisFeatureClassId',
      alignmentStyle: 'alignmentStyleId',
    };

    const fieldKey =
      colDef.field in relationFieldMap ? relationFieldMap[colDef.field as keyof typeof relationFieldMap] : colDef.field;

    await updateAlignmentSetting({ alignmentId: data.id, [fieldKey]: newValue });
  };

  const handleBulkDuplicatePropertyMappingsRecursive = async (
    originalDuplicateIdMap: Record<number, number>,
    idsToDuplicate: number[],
    cursor = 0,
  ) => {
    const { data } = await searchPropertyMappingsForAlignments({
      alignmentIds: idsToDuplicate,
      limit: 500,
      cursor,
    });

    const propertyMappingsToDuplicate = data.result.map(({ setting, attribute, civil3dProperty }) => ({
      alignmentSettingId: originalDuplicateIdMap[setting!.id],
      attributeId: attribute.id,
      civil3dPropertyId: civil3dProperty.id,
    }));
    if (propertyMappingsToDuplicate.length > 0) {
      await createPropertyMappings({ propertyMappings: propertyMappingsToDuplicate });
    }

    if (data.nextCursor) {
      await handleBulkDuplicatePropertyMappingsRecursive(originalDuplicateIdMap, idsToDuplicate, data.nextCursor);
    }
  };

  return (
    <ArcgisCivil3dConfigurationGrid
      featureType={Civil3dFeatureType.ALIGNMENT}
      error={updateError || deleteError || duplicateError || createPropertyMappingsError}
      loading={loadingAlignmentStyles}
      columns={columns}
      getSetting={async (alignmentId) => {
        const { data } = await getAlignmentSetting({
          alignmentId,
          include: [
            ArcgisCivil3dAlignmentSettingRelationship.CONFIGURATION,
            ArcgisCivil3dAlignmentSettingRelationship.ARCGIS_FEATURE_CLASS,
          ],
        });
        return data;
      }}
      onUpdateQuery={async (alignmentId, query) => {
        await updateAlignmentSetting({ alignmentId, query });
      }}
      onGetRows={getAlignmentSettings}
      onUpdateRow={updateSetting}
      onColumnResized={onColumnResized}
      onBulkDelete={async (settingIds) => {
        await deleteAlignmentSettings({ alignmentIds: settingIds });
      }}
      onBulkDuplicate={async (settingIds) => {
        const duplicatedRecords = await duplicateAlignmentSettings({ alignmentIds: settingIds });
        await handleBulkDuplicatePropertyMappingsRecursive(
          duplicatedRecords.reduce(
            (acc, { id, originalId }) => {
              if (!originalId) return acc;
              return { ...acc, [originalId]: id };
            },
            {} as Record<number, number>,
          ),
          settingIds,
        );
      }}
    />
  );
};
